/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import { Utils } from "../../utils/utils";
import domtoimage from "dom-to-image";
import { LangUtils } from "../../utils/lang-utils";
import { isEqual } from "lodash";
export class DiscoveryButtonComponent {
    constructor() {
        this.defOptions = new Param();
        this.innerVars = {};
        this.result = undefined;
        this.type = undefined;
        this.options = new Param();
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.url = undefined;
        this.language = 'warpscript';
        this.vars = '{}';
        this.parsing = false;
        this.rendering = false;
        this.label = 'Ok';
        this.innerStyle = undefined;
        this.active = undefined;
    }
    varsUpdate(newValue, oldValue) {
        var _a;
        let vars = this.vars;
        if (!!this.vars && typeof this.vars === 'string') {
            vars = JSON.parse(this.vars);
        }
        if (!isEqual(vars, this.innerVars)) {
            this.innerVars = vars;
            this.parseResult();
        }
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['varsUpdate'], { vars: this.vars, newValue, oldValue });
    }
    updateRes(newValue, oldValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
            this.parseResult();
        }
    }
    discoveryEventHandler(event) {
        const res = Utils.parseEventData(event.detail, this.options.eventHandler, this.el.id);
        if (res.style) {
            this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), res.style);
        }
        if (res.vars) {
            this.innerVars = Object.assign(Object.assign({}, this.innerVars), res.vars);
        }
    }
    async resize() {
        // empty
    }
    // noinspection JSUnusedLocalSymbols
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async export(type = 'png') {
        let bgColor = Utils.getCSSColor(this.el, '--warp-view-bg-color', 'transparent');
        bgColor = (this.options || { bgColor }).bgColor || bgColor;
        const dm = ((this.innerResult || {
            globalParams: { bgColor },
        }).globalParams || { bgColor });
        bgColor = dm.bgColor || bgColor;
        return await domtoimage.toPng(this.root, { height: this.height, width: this.width, bgcolor: bgColor });
    }
    componentWillLoad() {
        var _a;
        this.LOG = new Logger(DiscoveryButtonComponent, this.debug);
        this.parsing = true;
        if (typeof this.options === 'string') {
            this.options = JSON.parse(this.options);
        }
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            options: this.options,
        });
        this.parseResult();
        this.draw.emit();
    }
    parseResult() {
        this.innerResult = GTSLib.getData(this.result);
        const btnLabel = (this.options.button || { label: 'Ok' }).label;
        const dm = (this.innerResult || {
            globalParams: {
                button: { label: btnLabel },
            },
        }).globalParams || { button: { label: btnLabel } };
        this.label = dm.button.label;
        let options = Utils.mergeDeep(this.defOptions, this.options || {});
        options = Utils.mergeDeep(options || {}, this.innerResult.globalParams);
        this.options = Object.assign({}, options);
        if (!!this.vars && typeof this.vars === 'string') {
            this.innerVars = JSON.parse(this.vars);
        }
        else if (!!this.vars) {
            this.innerVars = this.vars;
        }
        if (this.options.customStyles) {
            this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), this.options.customStyles || {});
        }
        setTimeout(() => { var _a, _b; return this.active = (_b = (((_a = this.innerResult) === null || _a === void 0 ? void 0 : _a.data) || []).find(v => v.active)) === null || _b === void 0 ? void 0 : _b.value; });
    }
    handleClick() {
        var _a;
        const ws = LangUtils.prepare(`${this.innerResult.data} EVAL`, this.innerVars || {}, ((_a = this.options) === null || _a === void 0 ? void 0 : _a.skippedVars) || [], this.type, this.language);
        Utils.httpPost(this.url, ws, this.options.httpHeaders)
            .then(res => {
            var _a, _b;
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['handleClick', 'res.data'], res.data);
            const result = GTSLib.getData(res.data);
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['handleClick', 'getData'], result);
            if (!!result) {
                (result.events || []).forEach(e => {
                    var _a;
                    (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['handleClick', 'emit'], { discoveryEvent: e });
                    if (typeof e.value !== 'object' && GTSLib.isArray(e.value)) {
                        e.value = [e.value];
                    }
                    this.discoveryEvent.emit(Object.assign(Object.assign({}, e), { source: this.el.id }));
                });
            }
            this.execResult.emit(res.data);
        })
            .catch(e => {
            var _a;
            this.statusError.emit(e);
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.error(['exec'], e);
        });
    }
    toggle(value) {
        this.active = value;
        (this.innerResult.events || []).forEach(e => {
            var _a;
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['handleClick', 'emit'], { discoveryEvent: e });
            if (!e.value) {
                e.value = {};
            }
            e.value[e.selector] = value;
            this.discoveryEvent.emit(Object.assign(Object.assign({}, e), { source: this.el.id }));
        });
    }
    generateStyle(innerStyle) {
        return Object.keys(innerStyle || {}).map(k => k + ' { ' + innerStyle[k] + ' }').join('\n');
    }
    render() {
        var _a, _b;
        return [
            h("style", { key: '1a76acc321cf715d56752d6761e4e63cf202546e' }, this.generateStyle(this.innerStyle)),
            h("div", { key: 'c5fe4be86b662c8fa1676513cc1b2564ade91509', ref: el => this.root = el, class: "button-wrapper" }, this.type === 'button'
                ? h("button", { type: "button", class: "discovery-btn", innerHTML: this.label, onClick: () => this.handleClick() })
                : '', this.type === 'button:radio'
                ? h("div", { class: "discovery-btn-group" }, GTSLib.isArray((_a = this.innerResult) === null || _a === void 0 ? void 0 : _a.data)
                    ? (((_b = this.innerResult) === null || _b === void 0 ? void 0 : _b.data) || []).map(v => h("button", { type: "button", class: {
                            'discovery-btn': true,
                            'active': v.value === this.active,
                        }, innerHTML: v.label, onClick: () => this.toggle(v.value) })) : '') : ''),
        ];
    }
    static get is() { return "discovery-button"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-button.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-button.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "width": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "url": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "url",
                "reflect": false
            },
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'warpscript' | 'flows'",
                    "resolved": "\"flows\" | \"warpscript\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "language",
                "reflect": false,
                "defaultValue": "'warpscript'"
            },
            "vars": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "vars",
                "reflect": false,
                "defaultValue": "'{}'"
            }
        };
    }
    static get states() {
        return {
            "parsing": {},
            "rendering": {},
            "label": {},
            "innerStyle": {},
            "active": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "execResult",
                "name": "execResult",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any[]",
                    "resolved": "any[]",
                    "references": {}
                }
            }, {
                "method": "statusError",
                "name": "statusError",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "discoveryEvent",
                "name": "discoveryEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "DiscoveryEvent",
                    "resolved": "DiscoveryEvent",
                    "references": {
                        "DiscoveryEvent": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DiscoveryEvent"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: 'png' | 'svg') => Promise<string>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "vars",
                "methodName": "varsUpdate"
            }, {
                "propName": "result",
                "methodName": "updateRes"
            }];
    }
    static get listeners() {
        return [{
                "name": "discoveryEvent",
                "method": "discoveryEventHandler",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=discovery-button.js.map
